import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import '../CSS/VideoLibrary.css';

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const [polls, setPolls] = useState([]);
  const [activePoll, setActivePoll] = useState(null);
  const [answeredPollIds, setAnsweredPollIds] = useState([]);

  const videoRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const requestedVideo = params.get('video');

        const res = await fetch('/api/videos?category=Activist', {
          credentials: 'include',
          cache: 'no-store',
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || 'Unable to load videos');
        }

        const videoList = data.videos || [];
        setVideos(videoList);

        if (requestedVideo) {
          const decodedRequestedVideo = decodeURIComponent(requestedVideo).toLowerCase();

          const match = videoList.find(video =>
            video.name?.toLowerCase() === decodedRequestedVideo
          );

          setSelectedVideo(match || videoList[0] || null);
        } else {
          setSelectedVideo(videoList[0] || null);
        }
      } catch (err) {
        console.error('Error loading videos:', err);
        setErrorMessage(err.message || 'Error loading videos');
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, [location.search]);

  useEffect(() => {
    const loadPolls = async () => {
      if (!selectedVideo?.name) {
        setPolls([]);
        return;
      }

      try {
        setPolls([]);
        setActivePoll(null);
        setAnsweredPollIds([]);

        const res = await fetch(
          `/api/video-polls?videoTitle=${encodeURIComponent(selectedVideo.name)}`,
          {
            credentials: 'include',
            cache: 'no-store',
          }
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || 'Unable to load video polls');
        }

        setPolls(data.polls || []);
      } catch (err) {
        console.error('Error loading video polls:', err);
        setPolls([]);
      }
    };

    loadPolls();
  }, [selectedVideo]);

  const filteredVideos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return videos;

    return videos.filter(video =>
      video.name?.toLowerCase().includes(term)
    );
  }, [videos, searchTerm]);

  const selectedVideoUrl = selectedVideo
    ? `/api/videos/${encodeURIComponent(selectedVideo.name)}`
    : '';

  const handleTimeUpdate = () => {
    if (!videoRef.current || activePoll) return;

    const currentTime = Math.floor(videoRef.current.currentTime);

    const pollToShow = polls.find(poll => {
      const pollTime = Number(poll.TimestampSeconds);

      return (
        poll.Active !== false &&
        pollTime <= currentTime &&
        currentTime <= pollTime + 1 &&
        !answeredPollIds.includes(poll.id)
      );
    });

    if (pollToShow) {
      videoRef.current.pause();
      setActivePoll(pollToShow);
    }
  };

  const handlePollAnswer = async (answer) => {
  if (!activePoll) return;

  const currentPoll = activePoll;

  try {
    const payload = {
      videoText: selectedVideo?.name,
      pollItemId: Number(currentPoll.id),
      pollTitle: currentPoll.Title || '',
      question: currentPoll.Question,
      answer,
      timestampSeconds: Number(currentPoll.TimestampSeconds || 0),
    };

    console.log('Sending poll response:', payload);

    const res = await fetch('/api/video-poll-response', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    const text = await res.text();

    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      console.error('Non-JSON response from server:', text);
      throw new Error('Server returned a non-JSON response');
    }

    if (!res.ok) {
      console.error('Poll response API error:', data);
      throw new Error(data.message || 'Unable to save poll response');
    }

    setAnsweredPollIds(prev => [...prev, currentPoll.id]);
    setActivePoll(null);

    setTimeout(() => {
      videoRef.current?.play();
    }, 100);

  } catch (err) {
    console.error('Error saving poll answer:', err);
    alert(err.message || 'There was an issue saving your answer.');
  }
};

  const getPollOptions = (poll) => {
    return [
      poll.OptionA,
      poll.OptionB,
      poll.OptionC,
      poll.OptionD,
    ].filter(Boolean);
  };

  const formatFileSize = (size) => {
    if (!size) return 'Unknown size';

    const mb = size / 1024 / 1024;

    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }

    return `${mb.toFixed(1)} MB`;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'Unknown date';

    return new Date(dateValue).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="videos-page">
      <div className="videos-hero">
        <div>
          <p className="videos-eyebrow">Orion Media Library</p>
          <h1>Activism Video Collection</h1>
          <p className="videos-subtitle">
           A curated library of activist released media - highlighting movement developments, organizers activity, 
           and significant direct actions or protesting. It is of note that this collection is geared to providing 
           glimpses of notable events and tactics and does not consist of all known footage or webinars; but if 
           interested in additional material or other information please reach out via an RFI. 
          </p>
        </div>

        <div className="videos-count-card">
          <span>{videos.length}</span>
          <p>Total Videos</p>
        </div>
      </div>

      {loading && (
        <div className="videos-loading">
          Loading video library...
        </div>
      )}

      {!loading && errorMessage && (
        <div className="videos-error">
          {errorMessage}
        </div>
      )}

      {!loading && !errorMessage && videos.length === 0 && (
        <div className="videos-empty">
          No videos found.
        </div>
      )}

      {!loading && !errorMessage && videos.length > 0 && (
        <div className="videos-layout">
          <aside className="videos-sidebar">
            <div className="videos-search-box">
              <input
                type="text"
                placeholder="Search videos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="videos-list">
              {filteredVideos.length === 0 ? (
                <div className="videos-no-results">
                  No matching videos.
                </div>
              ) : (
                filteredVideos.map(video => (
                  <button
                    key={video.id}
                    className={`videos-list-item ${
                      selectedVideo?.id === video.id ? 'active' : ''
                    }`}
                    onClick={() => setSelectedVideo(video)}
                  >
                    <div className="videos-thumb">
                      ▶
                    </div>

                    <div className="videos-list-text">
                      <strong>{video.name}</strong>
                      <span>
                        {formatFileSize(video.size)} · {formatDate(video.createdDateTime)}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </aside>

          <main className="videos-main">
            {selectedVideo && (
              <div className="videos-player-card">
                <div className="videos-player-wrap">
                  <video
                    ref={videoRef}
                    key={selectedVideo.name}
                    controls
                    preload="metadata"
                    className="videos-player"
                    src={selectedVideoUrl}
                    onTimeUpdate={handleTimeUpdate}
                  />

                  {activePoll && (
                    <div className="video-poll-overlay">
                      <div className="video-poll-box">
                        <h3>{activePoll.Question}</h3>

                        <div className="video-poll-options">
  {getPollOptions(activePoll).map(option => (
    <button
      key={option}
      onClick={() => handlePollAnswer(option)}
    >
      {option}
    </button>
  ))}
</div>

{!activePoll.Required && (
  <button
    className="video-poll-skip"
    onClick={() => {
      setAnsweredPollIds(prev => [...prev, activePoll.id]);
      setActivePoll(null);

      setTimeout(() => {
        videoRef.current?.play();
      }, 100);
    }}
  >
    Skip Question
  </button>
)}
                      </div>
                    </div>
                  )}
                </div>

                <div className="videos-player-info">
                  <div>
  <h2>{selectedVideo.name}</h2>

  {selectedVideo.notes && (
    <div className="videos-notes">
      <h3>Description / Sources</h3>
      <p>{selectedVideo.notes}</p>
    </div>
  )}
</div>

                  <button
                    className="videos-open-link"
                    onClick={async () => {
                      try {
                        const shareLink =
                          `${window.location.origin}/activistvideos?video=${encodeURIComponent(selectedVideo.name)}`;

                        await navigator.clipboard.writeText(shareLink);

                        alert('Video link copied to clipboard');
                      } catch (err) {
                        console.error('Failed to copy video link:', err);
                        alert('Failed to copy link');
                      }
                    }}
                  >
                    Copy Video Link
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default Videos;